import { hasServer } from '@/core/session';
import { getWorldConn, loadWorldCache, pullWorld } from '@/modules/activity/trace';
import { syncOrbit } from './orbit';
import { syncOura } from './oura';
import { syncRenpho } from './renpho';
import { getTrips, loadTrips, syncTrips } from './trips';

// Pulls what's new from Trace, Trips App, Oura and Renpho (and sends sleep and workouts on to
// Orbit): when the app opens, when it comes back to the front, and every 30 minutes while it's open. Each source remembers its own last sync and
// error (shown in Settings → Connections); one failing never stops the others.

const EVERY_MS = 30 * 60_000;
const MIN_GAP_MS = 5 * 60_000;
let last = 0;
let running: Promise<void> | null = null;

export function syncConnections({ force = false } = {}): Promise<void> {
  if (running) return running;
  if (!force && Date.now() - last < MIN_GAP_MS) return Promise.resolve();
  last = Date.now();
  running = (async () => {
    // Trace and Trips App first: they say which time zone each moment was in. Trace talks to
    // its own hub; Trips App, Oura and Renpho go through this account's server.
    if (getWorldConn()) await pullWorld().catch(() => {});
    if (!hasServer()) return;
    if (getTrips()) await syncTrips().catch(() => {});
    await Promise.allSettled([syncOura(), syncRenpho()]);
    // Then Orbit gets the nights, now with the newest sleep and the zones above.
    await syncOrbit().catch(() => {});
  })().finally(() => {
    running = null;
  });
  return running;
}

let started = false;

export function startConnections() {
  void Promise.all([loadWorldCache(), loadTrips()]).then(() => syncConnections());
  if (started) return;
  started = true;
  setInterval(() => {
    if (document.visibilityState === 'visible') void syncConnections();
  }, EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void syncConnections();
  });
}
